// Renders the conversation header and message thread.

import { Fragment, useEffect, useRef, useState } from 'react';
import { MoreHorizontal, PanelLeft, PanelRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ViewerAvatar } from '@/components/shared/viewer-avatars';
import { MarkdownContent } from '@/components/markdown-content';
import { cn, initialsOf } from '@/lib/utils';
import type { TicketViewer } from '@/types/websocket.types';
import type { WorkspaceMessage } from './types';
import { presenceLabel, timeLabel } from './sentinel-workspace.helpers';
import { MessageAttachments } from './message-attachments';
import { EmailHtmlContent } from './email-html-content';

/**
 * An agent's email reply as its helpdesk shows it: puts back the browser defaults Tailwind's reset
 * strips (lists, spacing, links, headings, inline images), which email HTML is written for and Gorgias
 * renders sent replies with, at the bubble's text size. Bold, italic, underline and strike survive the
 * reset, so they need nothing. Markdown bubbles keep MARKDOWN_PROSE.
 */
const AGENT_EMAIL_CLASS = [
  'text-[13px] leading-[1.5] font-normal tracking-[0.02px] text-text-body wrap-break-word',
  '[&_p]:my-[1em] [&>:first-child]:mt-0 [&>:last-child]:mb-0',
  '[&_ul]:mb-[1em] [&_ul]:list-disc [&_ul]:pl-10 [&_ol]:mb-[1em] [&_ol]:list-decimal [&_ol]:pl-10',
  '[&_li_ul]:mb-0 [&_li_ol]:mb-0 [&_ul_ul]:list-[circle] [&_ul_ul_ul]:list-[square]',
  '[&_a]:text-primary [&_a]:underline',
  '[&_blockquote]:my-[1em] [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3',
  '[&_h1]:my-[0.67em] [&_h1]:text-base [&_h1]:font-bold [&_h2]:my-[0.83em] [&_h2]:font-bold [&_h3]:my-[1em] [&_h3]:font-bold',
  '[&_hr]:my-[1em] [&_hr]:border-border',
  '[&_img]:inline [&_img]:max-w-full [&_img]:h-auto',
  '[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto',
].join(' ');

const AGENT_BODY_CLASS =
  'text-[13px] leading-[normal] font-normal tracking-[0.02px] text-text-body [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:space-y-2';

const MESSAGE_BODY_CLASS = 'text-[13px] leading-normal text-text-quote [&_p]:mb-2 [&_p:last-child]:mb-0';

interface ConversationHeaderProps {
  /** Null while the ticket detail loads — the header shows a placeholder, not a bare "#". */
  displayId: string | null;
  listCollapsed: boolean;
  onExpandList: () => void;
  detailsCollapsed: boolean;
  onExpandDetails: () => void;
  /** Other people on this ticket right now. */
  viewers?: TicketViewer[];
  /** Controls on the right of the header, such as "Assign to me". */
  actions?: React.ReactNode;
  /** Beside the number: opens the ticket in its helpdesk. */
  helpdeskLink?: React.ReactNode;
}

export function ConversationHeader({
  displayId,
  listCollapsed,
  onExpandList,
  detailsCollapsed,
  onExpandDetails,
  viewers = [],
  actions,
  helpdeskLink,
}: ConversationHeaderProps) {
  const presence = presenceLabel(viewers);
  return (
    <>
      <div className="flex h-[60px] shrink-0 items-center justify-between border-b border-gray-500 bg-white/80 py-3 pl-4 pr-2.5">
        <div className="flex items-center gap-2">
          {listCollapsed && (
            <Button variant="ghost" size="icon-sm" onClick={onExpandList} className="text-gray-900" aria-label="Expand ticket list">
              <PanelLeft className="size-[14px]" />
            </Button>
          )}
          {displayId === null ? (
            <Skeleton className="h-5 w-20 rounded-md" />
          ) : (
            <div className="flex items-center gap-0.5">
              <p className="text-[16px] font-semibold leading-6 text-text-body"># {displayId}</p>
              {helpdeskLink}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {detailsCollapsed && (
            <Button variant="ghost" size="icon-sm" onClick={onExpandDetails} className="text-gray-900" aria-label="Expand details">
              <PanelRight className="size-[14px]" />
            </Button>
          )}
        </div>
      </div>
      {/* Always mounted: a live region only announces changes made after it exists. */}
      <div role="status" className="shrink-0">
        {/* A shade lighter than a private note's yellow, so the two are never confused. */}
        {presence && (
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 border-b border-yellow-200 bg-yellow-50 px-4 py-2 text-[12px] leading-4 text-text-prose">
            {presence.named.map((viewer, index) => {
              const last = index === presence.named.length - 1;
              return (
                <Fragment key={viewer.userId}>
                  {index > 0 && last && presence.others === 0 && <span>and</span>}
                  <span className="flex items-center gap-1.5">
                    <ViewerAvatar viewer={viewer} />
                    <span className="font-semibold text-text-body">
                      {viewer.name}
                      {!last && presence.named.length + presence.others > 2 && ','}
                    </span>
                  </span>
                </Fragment>
              );
            })}
            {presence.others > 0 && (
              <span>
                and {presence.others} {presence.others === 1 ? 'other' : 'others'}
              </span>
            )}
            <span>{presence.what}</span>
          </p>
        )}
      </div>
    </>
  );
}

/** Photo, falling back to initials. Explicit white fill: the default `bg-muted` matches the thread background. */
export function MessageAvatar({
  name,
  src,
  tone = 'default',
}: {
  name: string;
  src?: string | null;
  tone?: 'default' | 'customer' | 'agent';
}) {
  // The thread's avatars are 32px (Figma); elsewhere the compact 24px one stays.
  const large = tone !== 'default';
  return (
    <Avatar
      className={cn(
        'aspect-square shrink-0 rounded-full bg-white',
        large ? 'size-8' : 'size-6',
        tone === 'agent' ? 'border-[0.5px] border-blue-300' : 'border border-border',
      )}
    >
      {src && <AvatarImage src={src} alt={name} className="rounded-full object-cover" />}
      <AvatarFallback
        className={cn(
          'rounded-full font-medium',
          large ? 'text-[13px] leading-[18px]' : 'text-[10px]',
          tone === 'agent' ? 'bg-blue-100 text-blue-500' : 'bg-white text-text-quote',
        )}
      >
        {initialsOf(name)}
      </AvatarFallback>
    </Avatar>
  );
}

function EmailHeader({ email }: { email: NonNullable<WorkspaceMessage['email']> }) {
  const rows = [
    ['From', email.from],
    ['To', email.to],
    ['Subject', email.subject],
  ].filter((row): row is [string, string] => typeof row[1] === 'string');
  return (
    <div className="mb-3 flex flex-col gap-1 border-b border-gray-300 pb-3 text-[13px] leading-normal text-text-quote">
      {rows.map(([label, value]) => (
        <p key={label} className="break-all">
          <span className="font-semibold text-text-body">{label}:</span> {value}
        </p>
      ))}
    </div>
  );
}

/** `timeZone` is the brand's; without one, times are in the viewer's zone. */
interface ChatThreadProps {
  messages: WorkspaceMessage[];
  timeZone?: string;
  /** The AI run behind each sent reply; those replies get a View activity button. */
  activityRuns?: Map<number, number>;
  /** The run open in the Activity tab, so its reply's button reads as pressed. */
  selectedRunId?: number | null;
  onOpenActivity?: (runId: number) => void;
}

/** A customer's email, its quoted history behind a "…" toggle as Gmail and Gorgias show it. */
function CustomerEmail({ html, hasQuotedTail }: { html: string; hasQuotedTail: boolean }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <EmailHtmlContent html={html} quotedVisible={expanded} />
      {hasQuotedTail && (
        <button
          type="button"
          aria-label={expanded ? 'Hide quoted text' : 'Show quoted text'}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          className="mt-2 flex h-4 items-center rounded border border-gray-300 bg-gray-100 px-1 text-gray-700 hover:bg-gray-200"
        >
          <MoreHorizontal className="size-3.5" />
        </button>
      )}
    </>
  );
}

export function ChatThread({ messages, timeZone, activityRuns, selectedRunId, onOpenActivity }: ChatThreadProps) {
  const endRef = useRef<HTMLDivElement | null>(null);

  // Without this a long thread opens at the top and the newest message sits below the fold.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  return (
    <div className="flex flex-col gap-4 pt-4">
      {messages.map((message) =>
        message.from === 'customer' ? (
          <div key={message.id} className="flex items-start gap-1.5 pl-4 pr-24">
            <MessageAvatar name={message.senderName} src={message.avatarUrl} tone="customer" />
            {/* An email fills the bubble's 600px: its frame has no width of its own to fit to. */}
            <div
              className={cn(
                'min-w-0 max-w-[600px] rounded-2xl border border-gray-300 bg-white p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]',
                message.html ? 'w-full' : 'w-fit',
              )}
            >
              {message.email && <EmailHeader email={message.email} />}
              {/* The stored email HTML in a sandboxed frame when the helpdesk gave us one, else the cleaned text. */}
              {message.html ? (
                <CustomerEmail html={message.html} hasQuotedTail={message.hasQuotedTail === true} />
              ) : (
                <MarkdownContent text={message.markdown} className={MESSAGE_BODY_CLASS} />
              )}
              <MessageAttachments attachments={message.attachments} />
              <p className="mt-2 text-right text-[10px] text-gray-700">{timeLabel(message.createdAt, timeZone)}</p>
            </div>
          </div>
        ) : (
          <div key={message.id} className="group flex items-start justify-end gap-1.5 pl-24 pr-4">
            <div className="flex min-w-0 max-w-[600px] flex-col items-end gap-1">
              <div
                className={cn(
                  'flex min-w-0 w-fit max-w-[600px] flex-col gap-3 self-end rounded-2xl border p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]',
                  message.isPrivate
                    ? 'border-yellow-200 bg-yellow-100'
                    : message.isAutomated
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-blue-200 bg-blue-100',
                )}
              >
                {/* Inline, not framed: html is already formatting-only, and only Gorgias (and the odd Zendesk ticket) stores agent HTML, its quoted customer mail cut by the Gorgias quote classes. */}
                {message.html ? (
                  <div
                    className={AGENT_EMAIL_CLASS}
                    dangerouslySetInnerHTML={{ __html: message.html }}
                  />
                ) : (
                  <MarkdownContent text={message.markdown} className={AGENT_BODY_CLASS} />
                )}
                <MessageAttachments attachments={message.attachments} />
                <p className="text-[11px] leading-4 font-normal text-gray-800 self-end">
                  {message.isPrivate && 'Private note · '}
                  {timeLabel(message.createdAt, timeZone)}
                  {message.isAutomated && ' · Sent by AI'}
                </p>
              </div>
              {/* Same action as the Playground's reply: opens the run behind this reply in the side panel. */}
              {activityRuns?.has(message.id) && onOpenActivity && (
                <button
                  type="button"
                  onClick={() => onOpenActivity(activityRuns.get(message.id)!)}
                  aria-pressed={selectedRunId === activityRuns.get(message.id)}
                  className={cn(
                    '-mr-1.5 flex cursor-pointer items-center gap-1 rounded-lg px-1.5 py-1 text-xs text-gray-800 opacity-60 transition hover:bg-gray-200 hover:text-text-heading group-hover:opacity-100 focus-visible:opacity-100',
                    selectedRunId === activityRuns.get(message.id) && 'opacity-100',
                  )}
                >
                  <PanelRight className="size-3.5" />
                  View activity
                </button>
              )}
            </div>
            {message.isAutomated ? (
              <img src="/assets/ai/ai-sent-avatar.svg" alt="Sent by AI" width={32} height={32} className="shrink-0" />
            ) : (
              <MessageAvatar name={message.senderName} src={message.avatarUrl} tone="agent" />
            )}
          </div>
        ),
      )}
      <div ref={endRef} />
    </div>
  );
}
